/**
 * Connector Utilities
 *
 * Utility functions for applying connector fetch results to form data.
 */

import type { FetchResult } from '@services/connectorService';

/**
 * Consumer form data structure
 */
export interface ConsumerData {
  name?: string | null;
  phone?: string | null;
}

/**
 * Vehicle form data structure
 */
export interface VehicleData {
  referenceId?: string | null;
  vin?: string | null;
  stock?: string | null;
  year?: string | null;
  make?: string | null;
  model?: string | null;
  color?: string | null;
}

/**
 * Result of applying connector data to form fields
 */
export interface ConnectorMergeResult {
  consumerUpdates: Partial<ConsumerData>;
  vehicleUpdates: Partial<VehicleData>;
}

/**
 * Field names that can be excluded from a connector merge.
 * Use when the originating field shouldn't propagate certain attributes —
 * e.g. a VIN-triggered contract-detail fetch must not overwrite referenceId,
 * because the returned contract may be a stale/closed prior contract for
 * the same VIN.
 */
export type ConnectorMergeField =
  | keyof ConsumerData
  | keyof VehicleData;

/**
 * Determines whether a field should be updated based on overwriteExisting flag.
 *
 * @param existingValue - The current value of the field
 * @param overwriteExisting - Whether to overwrite existing values
 * @returns true if the field should be updated
 */
export function shouldUpdateField(
  existingValue: string | null | undefined,
  overwriteExisting: boolean
): boolean {
  return overwriteExisting || !existingValue;
}

/**
 * Merges connector fetch results into consumer and vehicle form data.
 *
 * @param fetchedData - The data fetched from the connector API
 * @param existingConsumer - Current consumer form data
 * @param existingVehicle - Current vehicle form data
 * @param overwriteExisting - Whether to overwrite existing field values
 * @returns Object containing updates for consumer and vehicle forms
 */
export function mergeConnectorData(
  fetchedData: FetchResult,
  existingConsumer: ConsumerData | null | undefined,
  existingVehicle: VehicleData | null | undefined,
  overwriteExisting: boolean = false,
  excludeFields: ReadonlyArray<ConnectorMergeField> = []
): ConnectorMergeResult {
  const consumerUpdates: Partial<ConsumerData> = {};
  const vehicleUpdates: Partial<VehicleData> = {};
  const excluded = new Set<string>(excludeFields);

  // Map customer fields
  if (!excluded.has('name') && fetchedData.customerName && shouldUpdateField(existingConsumer?.name, overwriteExisting)) {
    consumerUpdates.name = String(fetchedData.customerName);
  }
  if (!excluded.has('phone') && fetchedData.customerPhone && shouldUpdateField(existingConsumer?.phone, overwriteExisting)) {
    consumerUpdates.phone = String(fetchedData.customerPhone);
  }

  // Map vehicle fields
  if (!excluded.has('referenceId') && fetchedData.referenceNumber && shouldUpdateField(existingVehicle?.referenceId, overwriteExisting)) {
    vehicleUpdates.referenceId = String(fetchedData.referenceNumber);
  }
  if (!excluded.has('vin') && fetchedData.vin && shouldUpdateField(existingVehicle?.vin, overwriteExisting)) {
    vehicleUpdates.vin = String(fetchedData.vin);
  }
  if (!excluded.has('stock') && fetchedData.stockNumber && shouldUpdateField(existingVehicle?.stock, overwriteExisting)) {
    vehicleUpdates.stock = String(fetchedData.stockNumber);
  }
  if (!excluded.has('year') && fetchedData.year && shouldUpdateField(existingVehicle?.year, overwriteExisting)) {
    vehicleUpdates.year = String(fetchedData.year);
  }
  if (!excluded.has('make') && fetchedData.make && shouldUpdateField(existingVehicle?.make, overwriteExisting)) {
    vehicleUpdates.make = String(fetchedData.make);
  }
  if (!excluded.has('model') && fetchedData.model && shouldUpdateField(existingVehicle?.model, overwriteExisting)) {
    vehicleUpdates.model = String(fetchedData.model);
  }
  if (!excluded.has('color') && fetchedData.color && shouldUpdateField(existingVehicle?.color, overwriteExisting)) {
    vehicleUpdates.color = String(fetchedData.color);
  }

  return { consumerUpdates, vehicleUpdates };
}
